import { v4 as uuidv4 } from 'uuid';
import { IterationRule, RuleType } from 'schemas/IterationRule.schema.ts';
import { CampaignConfig } from 'schemas/CampaignConfig.schema.ts';
import { Iteration } from 'schemas/Iteration.schema.ts';
import { LibraryRuleMetadata } from 'schemas/LibraryRule.schema.ts';
import { getIterationRulesById, getRuleInIterationRules } from 'reducers/campaign/utils.ts';
import {
  FILTER_PRIORITY_LOWER_BOUND,
  FILTER_PRIORITY_UPPER_BOUND,
  MAX_NAME_FIELD_LENGTH,
  PRIORITY_STEP,
  ROUTING_PRIORITY_LOWER_BOUND,
  ROUTING_PRIORITY_UPPER_BOUND,
  SUPPRESSION_PRIORITY_LOWER_BOUND,
  SUPPRESSION_PRIORITY_UPPER_BOUND,
} from 'constants/rules.ts';
import {
  buildRulePathParam,
  getIterationRulesFromCampaign,
  matchIterationRuleByPriority,
} from './navigationUtils.ts';
import { YYYYMMDDToHumanReadable } from './formattingUtils.ts';

export type IterationRuleWithAndClauses = IterationRule & {
    AndClauses?: IterationRule[]
    DisplayName?: string;
    DisplayDescription?: string;
}

export const reduceRulesWithAndClauses = (acc: IterationRuleWithAndClauses[], rule: IterationRule) => {
  const existingAccumulatorRule = acc.find((accRule) => (accRule.Priority === rule.Priority) && (accRule.Type === rule.Type));

  if (!existingAccumulatorRule) {
    acc.push({
      ...rule,
      AndClauses: [],
    });
  } else {
    existingAccumulatorRule.AndClauses?.push(rule);
  }

  return acc;
};

export const getIterationAndRuleByIDs = (
  campaign: CampaignConfig,
  iterationId: string,
  ruleId: string,
  andClause?: string,
): { rule: IterationRule | undefined, iteration: Iteration | undefined } => {
  if (!campaign.CampaignConfig.Iterations) {
    return { rule: undefined, iteration: undefined };
  }

  const foundIteration = campaign.CampaignConfig.Iterations.find((iteration) => iteration.ID === iterationId);
  if (andClause && foundIteration) {
    const andClauseNumber = parseInt(andClause, 10);
    const andClauseRule = foundIteration.IterationRules?.find((rule) => (rule.Priority === foundIteration.IterationRules?.find((r) => r.ID === ruleId)?.Priority) && (rule.AndNumber === andClauseNumber));
    if (andClauseRule) {
      return { rule: andClauseRule, iteration: foundIteration };
    }
  }

  const foundRule = foundIteration?.IterationRules?.find((rule) => rule.ID === ruleId);
  return { rule: foundRule, iteration: foundIteration };
};

export const buildHrefForSuccessfulRuleSubmission = (
  campaign: CampaignConfig,
  iterationId: string,
  teamId: string,
  andClauseNumber: string | undefined,
  priority: number,
  ruleId: string,
  ruleTypeShortcode: RuleType,
  navigateToAndClause: boolean,
): string => {
  const currentIterationRules = getIterationRulesFromCampaign(campaign, iterationId);
  const nextAndClauseNumber = andClauseNumber ? parseInt(andClauseNumber, 10) + 1 : 1;
  const nextAndClause = currentIterationRules.find((rule) => (rule.Priority === priority) && (rule.AndNumber === nextAndClauseNumber));

  if (navigateToAndClause) {
    // If a rule exists with the same priority and an AND clause number, return a path to it
    if (nextAndClause) {
      return `/${teamId}/campaign/${campaign.CampaignConfig.ID}/iteration/${iterationId}/${buildRulePathParam(ruleTypeShortcode)}/${nextAndClause.ID}/and-clause/${nextAndClauseNumber}/library`;
    }

    // Otherwise, return a new AND clause for the same rule
    return `/${teamId}/campaign/${campaign.CampaignConfig.ID}/iteration/${iterationId}/${buildRulePathParam(ruleTypeShortcode)}/${ruleId}/and-clause/${nextAndClauseNumber}/library`;
  }

  if (ruleTypeShortcode === 'R') {
    return `/${teamId}/campaign/${campaign.CampaignConfig.ID}/iteration/${iterationId}/${buildRulePathParam(ruleTypeShortcode)}/${ruleId}/routing-ids`;
  }

  return `/${teamId}/campaign/${campaign.CampaignConfig.ID}/iteration/${iterationId}/summary?openRules=${ruleTypeShortcode}`;
};

export function getPriorityRangeByRuleType(ruleType: RuleType): { lowerBound: number, upperBound: number } {
  switch (ruleType) {
    case 'F':
      return { lowerBound: FILTER_PRIORITY_LOWER_BOUND, upperBound: FILTER_PRIORITY_UPPER_BOUND };
    case 'S':
      return { lowerBound: SUPPRESSION_PRIORITY_LOWER_BOUND, upperBound: SUPPRESSION_PRIORITY_UPPER_BOUND };
    case 'R':
      return { lowerBound: ROUTING_PRIORITY_LOWER_BOUND, upperBound: ROUTING_PRIORITY_UPPER_BOUND };
    default:
      return { lowerBound: FILTER_PRIORITY_LOWER_BOUND, upperBound: ROUTING_PRIORITY_UPPER_BOUND };
  }
}

// Add "And Number" to rules with the same priority,
// i.e. three rules with priority 500 will have AndNumber 0, 1 and 2 respectively
// This is not required for the final campaign configuration JSON payload,
// but allows us to differentiate between AND clauses in the UI.
export function augmentRulesWithAndNumbers(rules: IterationRule[]): IterationRule[] {
  const ruleGroups: Record<string, IterationRule[]> = {};

  // Create rule groups based on priority and rule type
  rules.forEach((rule) => {
    const key = `${rule.Priority}-${rule.Type}`;
    if (!ruleGroups[key]) {
      ruleGroups[key] = [];
    }
    ruleGroups[key].push({ ...rule });
  });

  const result: IterationRule[] = [];

  // iterate through each group item and assign AndNumber
  // based on the index of the rule in the group
  Object.values(ruleGroups).forEach((group) => {
    group.forEach((rule, index) => {
      result.push({ ...rule, AndNumber: index });
    });
  });

  return result;
}

export function sortRulesWithinTypeByPriority(rules: IterationRule[], ruleType: RuleType): IterationRule[] {
  const rulesByType = rules.filter((rule) => rule.Type === ruleType);
  const sortedRules = rulesByType.sort((a, b) => (a?.Priority ?? 0) - (b?.Priority ?? 0));

  return sortedRules;
}

export function getHighestPriorityByRuleType(
  rules: IterationRule[],
  defaultRules: IterationRule[],
  ruleType: RuleType,
): number {
  const allRules = [
    ...rules.filter((rule) => rule.Type === ruleType && (rule.AndNumber === 0 || rule.AndNumber === undefined)),
    ...defaultRules.filter((rule) => rule.Type === ruleType && (rule.AndNumber === 0 || rule.AndNumber === undefined)),
  ];

  if (allRules.length === 0) {
    return 0;
  }

  return Math.max(...allRules.map((rule) => rule.Priority ?? 0));
}

export function getNextRulePriorityForType(
  rules: IterationRule[],
  ruleType: RuleType,
  defaultRules: IterationRule[],
): number {
  const highestPriority = getHighestPriorityByRuleType(rules, defaultRules, ruleType);

  const { lowerBound } = getPriorityRangeByRuleType(ruleType);

  if (highestPriority === 0 || highestPriority < lowerBound) {
    return lowerBound + PRIORITY_STEP;
  }

  return highestPriority + PRIORITY_STEP;
}

export const getPickListValues = (rule: IterationRule) => {
  const controls = rule?.LibraryRuleMetadata?.MetaData?.Controls ?? [];
  const pickListControl = controls.find((control) => control.Type === 'PICK_LIST');
  const values = pickListControl?.Values;
  const comparatorValues = (rule.Comparator ?? '').split(',').map((v) => v.trim());
  return (values ?? []).filter((entry) => comparatorValues.includes(entry.Value));
};

export const getDisplayNameFromPickListValues = (rule: IterationRule): string => {
  const matchingEntries = getPickListValues(rule);

  const nameTemplate = rule?.LibraryRuleMetadata?.NameTemplate || [];
  const prefix = nameTemplate.filter((part) => part.Type === 'TEXT')
    .map((part) => part.Value)
    .join('');

  const labelValuePairs = matchingEntries.map((entry) => `${entry.Label} (${entry.Value})`);
  return `${prefix}${labelValuePairs.join(', ')}`;
};

export const getDisplayName = (rule: IterationRule | undefined, pattern: RegExp, replacement: string | null): string => {
  if (!rule?.Name) {
    return '';
  }

  const nameWithoutAndClauseSuffix = rule.Name.replace(/\s*-\s*\d+\s*$/i, '').trim();

  if (rule.LibraryRuleMetadata?.Type === 'PICK_LIST' && nameWithoutAndClauseSuffix.length > MAX_NAME_FIELD_LENGTH) {
    return getDisplayNameFromPickListValues(rule);
  }

  if (replacement) {
    return nameWithoutAndClauseSuffix.replace(pattern, replacement);
  }

  return nameWithoutAndClauseSuffix;
};

export const isNewStyleRule = (rule: IterationRule) => Boolean(rule.LibraryRuleMetadata);

export const isCustomRule = (rule: IterationRule) => !rule.LibraryRuleMetadata || rule.LibraryRuleMetadata?.Type === 'DEFAULT';

export function groupLibraryRulesByCategory(rules: LibraryRuleMetadata[]): [string, LibraryRuleMetadata[]][] {
  const groupedRules = rules.reduce<Record<string, LibraryRuleMetadata[]>>((acc, rule) => {
    const category = rule.Category?.trim() || 'Uncategorized';
    const existingCategoryRules = acc[category] ?? [];

    return {
      ...acc,
      [category]: [...existingCategoryRules, rule],
    };
  }, {});

  return Object.keys(groupedRules)
    .sort((first, second) => first.localeCompare(second))
    .map((category) => [
      category,
      groupedRules[category].toSorted((firstRule, secondRule) => firstRule.Name.localeCompare(secondRule.Name)),
    ]);
}

export function getRulesDisplay(iteration: Iteration, rules: IterationRule[]): IterationRuleWithAndClauses[] {
  const dateString = iteration.IterationDate
    ? YYYYMMDDToHumanReadable(iteration.IterationDate)
    : null;

  const pattern = /day of execution/i;
  const replacement = dateString ? `${dateString}` : null;

  return rules.map((rule) => {
    const displayName = getDisplayName(rule, pattern, replacement);

    if (isNewStyleRule(rule)) {
      const displayDescription = rule.Description;

      if (rule.LibraryRuleMetadata?.Type === 'PICK_LIST' || rule.LibraryRuleMetadata?.Type === 'LIST') {
        const nameTemplateText = rule.LibraryRuleMetadata?.NameTemplate?.find((part) => part.Type === 'TEXT');
        return {
          ...rule,
          DisplayName: nameTemplateText?.Value,
          DisplayDescription: displayDescription,
        };
      }

      return {
        ...rule,
        DisplayName: displayName,
        DisplayDescription: displayDescription,
      };
    }

    return {
      ...rule,
      DisplayName: displayName,
    };
  });
}

export interface UpdateRulePayload extends IterationRule {
  IterationID: string;
  AndNumber: number;
}
export const updateRule = (campaign: CampaignConfig, payload: UpdateRulePayload) => {
  const iterationID = payload.IterationID;
  const iterationRules = getIterationRulesById(campaign, iterationID!);

  if (!iterationRules) {
    return campaign;
  }

  const { IterationID: _ignored, ...updatedRulePayload } = structuredClone(
    payload as typeof payload & { IterationID?: string },
  );

  const existingRule = getRuleInIterationRules(
    iterationRules,
    payload.AndNumber!,
    payload.Priority,
    payload.Type,
  );

  const isParentRuleUpdate = payload.AndNumber === 0;

  if (!existingRule) {
    const newIterationRules = [
      ...iterationRules,
      updatedRulePayload,
    ];

    return {
      ...campaign,
      CampaignConfig: {
        ...campaign.CampaignConfig,
        Iterations: campaign.CampaignConfig.Iterations?.map(
          (iteration) => {
            if (iteration.ID === iterationID) {
              return {
                ...iteration,
                IterationRules: newIterationRules,
              };
            }
            return iteration;
          },
        ),
      },
    };
  }

  return {
    ...campaign,
    CampaignConfig: {
      ...campaign.CampaignConfig,
      Iterations: campaign.CampaignConfig.Iterations?.map(
        (iteration) => {
          if (iteration.ID === iterationID) {
            return {
              ...iteration,
              IterationRules:
                iteration.IterationRules?.map(
                  (rule) => {
                    if (
                      matchIterationRuleByPriority(
                        rule,
                        payload
                          .AndNumber!,
                        payload
                          .Priority as number,
                        payload
                          .Type as string,
                      )
                    ) {
                      return {
                        ...rule,
                        ...updatedRulePayload,
                      };
                    }

                    if (
                      isParentRuleUpdate
                      && rule.Priority === payload.Priority
                      && rule.Type === payload.Type
                      && rule.AndNumber !== 0
                    ) {
                      return {
                        ...rule,
                        Description: payload.Name,
                      };
                    }
                    return rule;
                  },
                ),
            };
          }
          return iteration;
        },
      ),
    },
  };
};

interface UpdateRulesPayload {
  IterationID: string;
  Type: string;
  Rules: IterationRule[];
}

export const updateRules = (campaign: CampaignConfig, payload: UpdateRulesPayload) => {
  const iterationID = payload.IterationID;
  const type = payload.Type;
  const newRules = payload.Rules;
  const iterations = campaign.CampaignConfig.Iterations || [];

  return {
    ...campaign,
    CampaignConfig: {
      ...campaign.CampaignConfig,
      Iterations: iterations.map((iteration) => {
        if (iteration.ID === iterationID) {
          const iterationRules = iteration.IterationRules || [];
          return {
            ...iteration,
            IterationRules: [
              ...iterationRules.filter((rule) => rule.Type !== type),
              ...newRules.filter((rule) => rule.Type === type),
            ].sort((a, b) => (a.Priority ?? 0) - (b.Priority ?? 0)),
          };
        }
        return iteration;
      }),
    },
  };
};

export const getSortedRuleByPriority = (rule: IterationRuleWithAndClauses) : IterationRuleWithAndClauses => {
  const hasAndClauses = Array.isArray(rule.AndClauses) && rule.AndClauses.length > 0;
  if (!hasAndClauses) {
    return {
      ...rule,
      AndClauses: [],
    };
  }

  const allClauses = [rule, ...(rule.AndClauses ?? [])];

  const sortedClauses = allClauses.toSorted((a, b) => (a.AndNumber ?? 0) - (b.AndNumber ?? 0));
  const parentClause = sortedClauses[0];
  const andClauses = sortedClauses.slice(1);

  return {
    ...parentClause,
    AndClauses: andClauses,
  };
};

export const getAllRulesSortedByPriority = (
  rules: IterationRuleWithAndClauses[],
): IterationRuleWithAndClauses[] => rules
  .map(getSortedRuleByPriority)
  .toSorted((a, b) => (a.Priority ?? 0) - (b.Priority ?? 0));

export const buildCreateNewRulePath = (
  baseCreateNewRulePath: string,
  ruleId?: string,
  andClauseNumber?: string,
): string => {
  if (andClauseNumber !== undefined) {
    return `${baseCreateNewRulePath}/${ruleId}/and-clause/${andClauseNumber}`;
  }

  if (ruleId && ruleId !== 'new') {
    return `${baseCreateNewRulePath}/${ruleId}`;
  }

  return `${baseCreateNewRulePath}/new`;
};

export const buildChangeLibraryRulePath = (
  baseRulePath: string,
  ruleId?: string,
  andClauseNumber?: number,
): string => {
  if (andClauseNumber !== undefined) {
    return `${baseRulePath}/${ruleId}/and-clause/${andClauseNumber}/library`;
  }

  if (ruleId) {
    return `${baseRulePath}/${ruleId}/library`;
  }

  return `${baseRulePath}/new/library`;
};

export const getCopyRuleName = (rules: IterationRuleWithAndClauses[], name?: string) => {
  const baseName = name?.replace(/ \(Copy \d+\)$/, '') ?? '';

  const copyCount = rules.filter((rule) => rule.Name?.startsWith(baseName)).length;

  return `${baseName} (Copy ${copyCount})`;
};

export const buildRuleCopy = (
  rules: IterationRuleWithAndClauses[],
  parentRule: IterationRuleWithAndClauses,
): IterationRuleWithAndClauses => {
  const newName = getCopyRuleName(rules, parentRule.Name);

  const newRule: IterationRuleWithAndClauses = {
    ...parentRule,
    ID: uuidv4(),
    Name: newName,
    AndClauses: parentRule.AndClauses?.map((andClause) => ({
      ...andClause,
      ID: uuidv4(),
    })),
  };

  return newRule;
};

export const getRulesWithCopyInserted = (
  rules: IterationRuleWithAndClauses[],
  parentRule: IterationRuleWithAndClauses,
) => {
  const newRule = buildRuleCopy(rules, parentRule);

  const parentIndex = rules.findIndex(
    (rule) => rule.Priority === parentRule.Priority
     && rule.Type === parentRule.Type,
  );

  if (parentIndex === -1) {
    throw new Error('parentRule not found in rules');
  }

  return [
    ...rules.slice(0, parentIndex + 1),
    newRule,
    ...rules.slice(parentIndex + 1),
  ];
};
